/**
 * persistence for document projects.
 *
 * `updateProjectMembers` is also where a membership write is REFUSED when the
 * set has moved under the caller: it re-reads the record before writing, so it
 * holds the only authoritative copy of the current members, and a caller that
 * states what it built against (`expectedMembers`) gets compared against that
 * rather than against a client cache. The same re-read is what the audit
 * trail's membership diff is taken from (`previousMembers`, VE-31977), for
 * the same reason: by the time anything downstream sees the success, the
 * reducer has written the new list over the old.
 *
 * `readMergedSetStaleness` (VE-31438) is the read side of the same record for
 * a DOWNLOAD GATE: the membership, the two stamps and the cached page spans in
 * one request, from two `details` blobs and no body. See
 * `mergedSetStaleness.ts` for why the gate cannot afford anything more.
 *
 * `readProjectIfProject` is the read side for the editor ROUTE, which is
 * handed a bare tdoId and has to know whether it names a project before it
 * opens anything. It returns rather than dispatching, because its caller is
 * deciding rather than displaying.
 */
import { Dispatch } from '@reduxjs/toolkit';
import callGraphQLApi from '@helpers/callGraphQLApi';
import { lookupLatestCaseSchemaId } from '@cbsa-modules/universal';
import { TDOId } from '@common-modules/universal/models/Brands';

import { PROJECT_REDACTION_ASSET_TYPE } from './projectRedactionAsset';
import {
  buildMergedSetStaleness,
  UNREAD_MERGED_SET,
  type MergedSetStaleness,
} from './mergedSetStaleness';
import {
  buildNewProjectDetails,
  ProjectKind,
  ProjectPointer,
  type ProjectDetails,
  parseProjectDetails,
  withMembers,
} from './models';
import {
  UPDATE_PROJECT_TDO_FAILURE,
  UPDATE_PROJECT_TDO_SUCCESS,
  CREATE_PROJECT_TDO_FAILURE,
  CREATE_PROJECT_TDO_INIT,
  CREATE_PROJECT_TDO_SUCCESS,
  DELETE_PROJECT_TDO_FAILURE,
  DELETE_PROJECT_TDO_INIT,
  DELETE_PROJECT_TDO_SUCCESS,
  WRITE_PROJECT_POINTERS_FAILURE,
  LOAD_PROJECT_FAILURE,
  LOAD_PROJECT_SUCCESS,
  WRITE_PROJECT_POINTERS_SUCCESS,
} from './actions';

const NOOP = 'noop';
const NOOP_ACTIONS = [NOOP, NOOP, NOOP] as const;

const CREATE_TDO_ACTIONS = [
  CREATE_PROJECT_TDO_INIT,
  CREATE_PROJECT_TDO_SUCCESS,
  CREATE_PROJECT_TDO_FAILURE,
] as never;

const DELETE_TDO_ACTIONS = [
  DELETE_PROJECT_TDO_INIT,
  DELETE_PROJECT_TDO_SUCCESS,
  DELETE_PROJECT_TDO_FAILURE,
] as never;

// These services are called from thunks that already hold the loose redux
// `getState`, matching the rest of casemedia.
type GetState = () => unknown;

const createProjectTdoMutation = `
  mutation createProjectTdo($input: CreateTDO!) {
    createTDO(input: $input) {
      id
      name
      details
    }
  }
`;

// `id` is `ID`, not `String` — a `String!` variable is not a compatible
// usage there and the server rejects the whole document at validation, so
// this read failed on every call before the types matched.
const updateProjectTdoMutation = `
  mutation updateProjectTdo($id: ID!, $details: JSONData!) {
    updateTDO(input: { id: $id, details: $details }) {
      id
      details
    }
  }
`;

const readTdoDetailsQuery = `
  query readProjectTdoDetails($id: ID!) {
    temporalDataObject(id: $id) {
      id
      details
    }
  }
`;

/**
 * The project TDO's own details and its redaction asset's, in ONE read.
 *
 * Both are `details` blobs that come back with the record, so the whole
 * download-gate context costs a single request and never a body download —
 * see `mergedSetStaleness.ts` for why that matters.
 */
const readMergedSetStalenessQuery = `
  query readMergedSetStaleness($id: ID!) {
    temporalDataObject(id: $id) {
      id
      details
      redactionSettings: assets(assetType: "${PROJECT_REDACTION_ASSET_TYPE}", orderBy: createdDateTime, orderDirection: desc, limit: 1) {
        records {
          id
          details
        }
      }
    }
  }
`;

const deleteProjectTdoMutation = `
  mutation deleteProjectTdo($id: ID!) {
    deleteTDO(id: $id) {
      id
      message
    }
  }
`;

const readCaseSdoQuery = `
  query readCaseSdo($schemaId: ID!, $id: ID!) {
    structuredDataObjects(schemaId: $schemaId, id: $id) {
      records {
        id
        data
      }
    }
  }
`;

const upsertCaseSdoMutation = `
  mutation upsertCaseSdo($sdoId: ID!, $schemaId: ID!, $data: JSONData!) {
    createStructuredData(input: { id: $sdoId, schemaId: $schemaId, data: $data }) {
      id
    }
  }
`;

interface CreateProjectTdoResponse {
  createTDO?: { id: TDOId; name: string; details: unknown } | null;
}

interface ReadTdoDetailsResponse {
  temporalDataObject?: { id: TDOId; details: unknown } | null;
}

interface ReadCaseSdoResponse {
  structuredDataObjects?: {
    records?: { id: string; data?: unknown }[] | null;
  } | null;
}

/**
 * Mint the TDO that holds a request's project.
 */
export const createProjectTdo =
  (payload: {
    readonly parentFolderId: string;
    readonly name: string;
    readonly kind: ProjectKind;
    readonly members: readonly TDOId[];
    readonly createdBy?: string;
    /**
     * Each member's original page count, for the project's cache. Supplied by
     * the CALLER for the same reason `updateProjectMembers` takes it: the
     * files are reachable from the saga, and looking them up here pulls the
     * app and user-permissions graph into a module that otherwise only talks
     * to GraphQL.
     */
    readonly pageCounts?: ReadonlyMap<string, number | null>;
  }) =>
  async (
    dispatch: Dispatch,
    getState: GetState
  ): Promise<TDOId | undefined> => {
    const now = new Date().toISOString();
    const response = await callGraphQLApi<CreateProjectTdoResponse>({
      actionTypes: CREATE_TDO_ACTIONS,
      query: createProjectTdoMutation,
      variables: {
        input: {
          parentFolderId: payload.parentFolderId,
          name: payload.name,
          startDateTime: now,
          stopDateTime: now,
          addToIndex: false,
          details: buildNewProjectDetails({
            kind: payload.kind,
            members: payload.members,
            now,
            createdBy: payload.createdBy,
            pageCountOf: payload.pageCounts
              ? (tdoId) => payload.pageCounts?.get(String(tdoId)) ?? null
              : undefined,
          }),
        },
      },
      dispatch,
      getState,
      // NOT `throwOnError`: the saga learns the outcome from
      // CREATE_PROJECT_TDO_FAILURE, which callGraphQLApi dispatches either
      // way. Throwing as well would surface as an unhandled rejection from the
      // `put`, since a put thunk's promise has no owner.
    });
    return response?.createTDO?.id ?? undefined;
  };

/**
 * Rewrite a project's membership.
 */
export const updateProjectMembers =
  (payload: {
    readonly projectTdoId: TDOId;
    readonly members: readonly TDOId[];
    readonly expectedMembers?: readonly TDOId[];
    /**
     * Each member's ORIGINAL page count, for the project's own cache.
     *
     * Supplied by the caller because it is the one holding the files. A
     * member left out of the map keeps whatever the project already stored.
     */
    readonly pageCounts?: ReadonlyMap<string, number | null>;
  }) =>
  async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    try {
      const current = await callGraphQLApi<ReadTdoDetailsResponse>({
        actionTypes: NOOP_ACTIONS,
        query: readTdoDetailsQuery,
        variables: { id: payload.projectTdoId },
        dispatch,
        getState,
        throwOnError: true,
      });
      const project = parseProjectDetails(current?.temporalDataObject?.details);
      if (!project) {
        // The pointer named something that is not a readable project. Building
        // one from scratch here would silently reset its created-by and
        // created-at, so report instead.
        dispatch(
          UPDATE_PROJECT_TDO_FAILURE({
            projectTdoId: payload.projectTdoId,
            reason: 'not-a-project',
          })
        );
        return;
      }
      // THE OUTCOME IS DISPATCHED HERE, not by the API helper, so it can carry
      // the project it is about — the saga matches on it. The helper's tuple
      // hands the raw response through and has nowhere to put a correlation
      // key.
      /**
       * REFUSE A LIST BUILT AGAINST A SET THAT HAS SINCE MOVED.
       *
       * THIS is where the check belongs, and the component's version of it
       * was inert: it froze a baseline from the `membersByProject` cache and
       * then compared it against that same cache, which nothing can change
       * while the dialog is open. It read as protection and could not fire.
       *
       * Here the comparison has something to compare against — `project` was
       * just re-read from the server, two lines up, and its members are the
       * record's own. So this sees another tab and another reviewer, which
       * is the race that actually happens, and it covers every caller that
       * states an expectation rather than only the one that asked.
       *
       * Ordered, because the order IS the membership: it sets page ranges
       * and the Bates run.
       */
      const expected = payload.expectedMembers;
      if (expected) {
        const current = project.members.map((member) => String(member.tdoId));
        const moved =
          current.length !== expected.length ||
          current.some((id, at) => id !== String(expected[at]));
        if (moved) {
          dispatch(
            UPDATE_PROJECT_TDO_FAILURE({
              projectTdoId: payload.projectTdoId,
              reason: 'moved-under-us',
            })
          );
          return;
        }
      }
      // ONE STAMP, WRITTEN AND REPORTED. It used to be minted here and minted
      // AGAIN in the reducer on success, so the record and the store held two
      // different readings of the same clock, differing by the round trip.
      const decidedAt = new Date().toISOString();
      await callGraphQLApi({
        actionTypes: NOOP_ACTIONS,
        query: updateProjectTdoMutation,
        variables: {
          id: payload.projectTdoId,
          details: {
            isProject: true,
            project: withMembers(
              project,
              payload.members,
              decidedAt,
              // HANDED IN, not looked up here. Reaching for the files from
              // this module pulled `casemedia/selectors` — and through it the
              // whole app and user-permissions graph — into a service that
              // otherwise talks only to GraphQL. The caller already knows the
              // files; a member missing from the map reads as null, which
              // `withMembers` treats as "leave what is stored" rather than
              // "erase it", so a write made before a member's detail has
              // landed does not drop a count the project already knew.
              (tdoId) => payload.pageCounts?.get(String(tdoId)) ?? null
            ),
          },
        },
        dispatch,
        getState,
        throwOnError: true,
      });
      dispatch(
        UPDATE_PROJECT_TDO_SUCCESS({
          projectTdoId: payload.projectTdoId,
          decidedAt,
          // The record's OWN membership as of the re-read above, for the
          // audit trail's diff (VE-31977). Read here because this is the one
          // place that holds it: by the time the saga sees the success the
          // reducer has already written the new list over the old.
          previousMembers: project.members.map((member) => member.tdoId),
        })
      );
    } catch {
      // ONE EXIT FOR EVERY FAILURE, read or write. `callGraphQLApi` throws on
      // a GraphQL error whether or not `throwOnError` is set, so a write that
      // failed used to leave this function by an exception that nothing
      // caught as a failure — the saga's race was answered only because the
      // helper had also dispatched its own tuple action.
      dispatch(
        UPDATE_PROJECT_TDO_FAILURE({
          projectTdoId: payload.projectTdoId,
          reason: 'write-failed',
        })
      );
    }
  };

/**
 * IS THIS TDO A PROJECT, and if so what is in it?
 *
 * For the editor route, which is handed a bare tdoId and has to decide what it
 * is looking at before it opens anything. A merged document's URL names the
 * PROJECT, and a project has no original to render — so the route opens a
 * member's session underneath and shows the merged output. It cannot make that
 * choice without this answer.
 *
 * RETURNS rather than dispatching, unlike `readProjectMembers` beside it. The
 * caller is deciding, not displaying, and a slice write would claim this
 * project is the open request's before anything has established that.
 *
 * `null` for a TDO that is not a project, and for a read that failed. Those
 * are the same answer HERE — both mean "nothing says to treat this as a merged
 * document", and the route then opens the tdoId as the document it was always
 * going to be. A failed read must not strand a reviewer on a loader.
 */
export const readProjectIfProject =
  (payload: { readonly tdoId: TDOId }) =>
  async (
    dispatch: Dispatch,
    getState: GetState
  ): Promise<ProjectDetails | null> => {
    try {
      const current = await callGraphQLApi<ReadTdoDetailsResponse>({
        actionTypes: NOOP_ACTIONS,
        query: readTdoDetailsQuery,
        variables: { id: payload.tdoId },
        dispatch,
        getState,
        throwOnError: true,
      });
      return parseProjectDetails(current?.temporalDataObject?.details);
    } catch {
      return null;
    }
  };

/**
 * Read a project's membership into the slice, for the edit dialog's checkboxes.
 */
export const readProjectMembers =
  (payload: { readonly projectTdoId: TDOId }) =>
  async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    try {
      const current = await callGraphQLApi<ReadTdoDetailsResponse>({
        actionTypes: NOOP_ACTIONS,
        query: readTdoDetailsQuery,
        variables: { id: payload.projectTdoId },
        dispatch,
        getState,
        throwOnError: true,
      });
      const project = parseProjectDetails(current?.temporalDataObject?.details);
      if (!project) {
        dispatch(LOAD_PROJECT_FAILURE({ projectTdoId: payload.projectTdoId }));
        return;
      }
      dispatch(
        LOAD_PROJECT_SUCCESS({
          projectTdoId: payload.projectTdoId,
          members: project.members.map((member) => member.tdoId),
          modifiedDateTime: project.modifiedDateTime,
        })
      );
    } catch {
      dispatch(LOAD_PROJECT_FAILURE({ projectTdoId: payload.projectTdoId }));
    }
  };

/**
 * Read what a merged document says about its members, for a download gate.
 *
 * FAILS TO "UNKNOWN", not to "stale": every verdict built from
 * `UNREAD_MERGED_SET` is `unknown`, which raises no warning and clears none.
 * The same posture the per-file half of the gate already takes — a read that
 * did not happen must not block a download the reviewer is entitled to.
 */
export const readMergedSetStaleness =
  (payload: { readonly projectTdoId: TDOId }) =>
  async (
    dispatch: Dispatch,
    getState: GetState
  ): Promise<MergedSetStaleness> => {
    try {
      const result = await callGraphQLApi<{
        temporalDataObject?: {
          details?: unknown;
          redactionSettings?: {
            records?: ReadonlyArray<{ details?: unknown }> | null;
          } | null;
        } | null;
      }>({
        actionTypes: NOOP_ACTIONS,
        query: readMergedSetStalenessQuery,
        variables: { id: payload.projectTdoId },
        dispatch,
        getState,
        throwOnError: true,
      });
      const tdo = result?.temporalDataObject;
      return buildMergedSetStaleness(
        tdo?.details,
        tdo?.redactionSettings?.records?.[0]?.details
      );
    } catch (err) {
      console.warn(
        'Merged-set staleness read failed; judging files alone',
        err
      );
      return UNREAD_MERGED_SET;
    }
  };

/**
 * Delete a project's TDO.
 */
export const deleteProjectTdo =
  (payload: { readonly projectTdoId: TDOId }) =>
  async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    await callGraphQLApi({
      actionTypes: DELETE_TDO_ACTIONS,
      query: deleteProjectTdoMutation,
      variables: { id: payload.projectTdoId },
      dispatch,
      getState,
    });
  };

/**
 * The project TDO's own name — what an operator sees in the aiWARE console.
 */
export const buildProjectTdoName = (requestName: string): string => {
  const trimmed = requestName.trim();
  // THE REQUEST'S NAME, unadorned. This is the merged document's display name
  // now — shown in the editor header and renameable there — so a suffix would
  // be something the reviewer has to delete before typing their own. It is
  // also what "reset" restores, and resetting to `X_Merged` would be odd when
  // the thing resetting is already known to be the merged document.
  return trimmed || 'Merged document';
};

/**
 * Record the request's project pointers.
 */
export const writeProjectPointers =
  (payload: {
    readonly caseSdoId: string;
    readonly pointers: readonly ProjectPointer[];
  }) =>
  async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    // EVERY exit from here dispatches one of the WRITE_PROJECT_POINTERS
    // outcomes. The saga is parked on a race between them, so a path that
    // returns or throws without dispatching leaves it waiting forever — and
    // with `takeLeading` on the watcher, that also wedges the control for the
    // rest of the session.
    try {
      const schemaId = await lookupLatestCaseSchemaId(dispatch, getState);
      if (!schemaId) {
        // An unprovisioned registry is "this environment cannot do that", not
        // a transient error — but it is still a failure for this write.
        dispatch(
          WRITE_PROJECT_POINTERS_FAILURE({
            caseSdoId: payload.caseSdoId,
            reason: 'no-schema',
          })
        );
        return;
      }
      const current = await callGraphQLApi<ReadCaseSdoResponse>({
        actionTypes: NOOP_ACTIONS,
        query: readCaseSdoQuery,
        variables: { schemaId, id: payload.caseSdoId },
        dispatch,
        getState,
        throwOnError: true,
      });
      const existing = current?.structuredDataObjects?.records?.[0]?.data as
        | Record<string, unknown>
        | undefined;
      if (!existing) {
        // REFUSE rather than fall through with `{}`. The upsert below replaces
        // the whole document, so writing an empty base would destroy the
        // request's name, status, priority and trash retention and leave a
        // record holding nothing but `projects` — the case row would then
        // render nameless and could not be restored from Trash.
        dispatch(
          WRITE_PROJECT_POINTERS_FAILURE({
            caseSdoId: payload.caseSdoId,
            reason: 'no-record',
          })
        );
        return;
      }
      await callGraphQLApi({
        // See the note in `updateProjectMembers`: the outcome is dispatched
        // below so it can name the record it wrote.
        actionTypes: NOOP_ACTIONS,
        query: upsertCaseSdoMutation,
        variables: {
          sdoId: payload.caseSdoId,
          schemaId,
          data: {
            ...existing,
            projects: payload.pointers,
            modifiedDateTime: new Date().toISOString(),
          },
        },
        dispatch,
        getState,
        throwOnError: true,
      });
      dispatch(
        WRITE_PROJECT_POINTERS_SUCCESS({ caseSdoId: payload.caseSdoId })
      );
    } catch {
      // Reached by the READ's `throwOnError` — deliberate there, since a
      // failed read must not fall through to a write that would replace the
      // record with `{ projects }` alone and erase the request's name, status
      // and retention — and now by the WRITE's too, so a failed upsert leaves
      // by the same door instead of as an unowned rejection.
      dispatch(
        WRITE_PROJECT_POINTERS_FAILURE({
          caseSdoId: payload.caseSdoId,
          reason: 'write-failed',
        })
      );
    }
  };
